import React from "react";
import { isEmbedded } from "src/utils/appBridgeInit";

import SettingsBootSkeleton from "./SettingsBootSkeleton";
import type { SettingsTabId } from "./SettingsPageSkeleton";

const DEFAULT_SETTINGS_TAB: SettingsTabId = "EtsyChannelSetting";

const SETTINGS_URL_TO_TAB_MAP: Record<string, SettingsTabId> = {
  etsy_config: "EtsyChannelSetting",
  api_configuration: "ApiConfiguration",
  product_settings: "ProductSettings",
  order_settings: "OrderSettings",
  notifications: "NotificationSettings",
};

const getSettingsTabFromLocation = (): SettingsTabId => {
  if (typeof window === "undefined") return DEFAULT_SETTINGS_TAB;

  const params = new URLSearchParams(window.location.search);
  const param = params.get("selected_tab") || params.get("selected") || "";

  if (!param) return DEFAULT_SETTINGS_TAB;

  const parts = param.split("/").filter(Boolean);
  const key = parts.length > 0 ? parts[parts.length - 1] : param;

  return SETTINGS_URL_TO_TAB_MAP[key] || SETTINGS_URL_TO_TAB_MAP[parts[0]] || DEFAULT_SETTINGS_TAB;
};

const SidebarSkeleton = () => (
  <aside
    style={{
      width: 156,
      minWidth: 156,
      borderRight: "1px solid #e1e3e5",
      background: "#fff",
      padding: "12px 10px",
      boxSizing: "border-box",
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <div style={{ width: "100%", height: 18, borderRadius: 6, background: "#e3e4e5" }} />
    </div>
    {Array.from({ length: 8 }, (_, index) => (
      <div
        key={index}
        style={{
          width: index === 0 ? "100%" : "88%",
          height: 14,
          borderRadius: 6,
          background: index === 0 ? "#c9d8f4" : "#eceef0",
          marginBottom: 10,
        }}
      />
    ))}
  </aside>
);

/** Route-level settings loading UI for boot / rehydrate (no Polaris AppProvider required). */
export const SettingsLoadingShell: React.FC = () => {
  const embedded = isEmbedded();
  const selectedTab = getSettingsTabFromLocation();

  if (embedded) {
    return (
      <div role="status" aria-live="polite" aria-label="Loading settings">
        <div style={{ padding: "16px 16px 8px" }}>
          <SettingsBootSkeleton selectedTab={selectedTab} showPageChrome={false} />
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading settings"
      style={{
        minHeight: "100vh",
        display: "flex",
        background: "#f6f6f7",
      }}
    >
      <SidebarSkeleton />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: "16px",
          boxSizing: "border-box",
        }}
      >
        <SettingsBootSkeleton selectedTab={selectedTab} />
      </div>
    </div>
  );
};

export default SettingsLoadingShell;
